import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CellHistoryChart } from "@/components/battery/CellHistoryChart";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Circle,
  BatteryCharging,
  Loader2,
  Thermometer,
  History,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell as ReCell,
} from "recharts";
import TopNav from "@/components/TopNav";
import PeriodPicker from "@/components/PeriodPicker";
import Gauge from "@/components/Gauge";
import { getDevice, getBatteryProfile, getCellProfile, getCellTempProfile, getStateLog } from "@/lib/api";
import { kyivDayStartMs, kyivTicks, fmtKyivHM } from "@/lib/time";

const online = (iso) => iso && Date.now() - new Date(iso).getTime() < 60000;

const METRICS = [
  { key: "soc", name: "Заряд", unit: "%", color: "#10B981" },
  { key: "voltage", name: "Напруга", unit: "В", color: "#EAB308" },
  { key: "imbalance", name: "Дисбаланс", unit: "мВ", color: "#F59E0B" },
  { key: "current", name: "Струм", unit: "А", color: "#3B82F6" },
];

const CELL_COLORS = [
  "#EF4444", "#F97316", "#F59E0B", "#EAB308", "#84CC16", "#22C55E",
  "#10B981", "#14B8A6", "#06B6D4", "#0EA5E9", "#3B82F6", "#6366F1",
  "#8B5CF6", "#A855F7", "#D946EF", "#EC4899",
];

// BMS state enumerations (from A1103 user manual). [label, tone]
const BMS_STATE = {
  1: ["Ініціалізація", "info"],
  2: ["Працює", "ok"],
  3: ["Відновлення роботи", "warn"],
  4: ["Критично низька напруга", "bad"],
  5: ["Критично висока напруга", "bad"],
  9: ["Помилка", "bad"],
};
const CHARGE_STATE = {
  0: ["Не дозволено", "idle"],
  1: ["Очікування дозволу", "info"],
  2: ["Дозволено", "ok"],
  3: ["Призупинено (балансування)", "info"],
  4: ["Закінчено (заряджена)", "ok"],
  5: ["Низька температура", "warn"],
  6: ["Висока температура", "warn"],
  7: ["Напруга комірки вище макс.", "bad"],
  8: ["Помилка (напруга < критичної)", "bad"],
};
const DISCHARGE_STATE = {
  0: ["Не дозволено", "idle"],
  1: ["Очікування дозволу", "info"],
  2: ["Дозволено", "ok"],
  3: ["Призупинено", "info"],
  4: ["Закінчено (розряджена)", "ok"],
  5: ["Низька температура", "warn"],
  6: ["Висока температура", "warn"],
};
const TONE_CLASS = {
  ok: "text-emerald-600",
  info: "text-sky-600",
  warn: "text-amber-500",
  bad: "text-red-600",
  idle: "text-slate-500",
};
function StatusValue({ map, code }) {
  if (code == null) return <span className="text-slate-400">—</span>;
  const entry = map[code];
  const [label, tone] = entry || [`Код ${code}`, "idle"];
  return (
    <span className={`font-semibold ${TONE_CLASS[tone] || "text-slate-900"}`}>
      {label}
    </span>
  );
}

// Error/shutdown codes (from A1103 user manual), human-readable Ukrainian.
const ERR_LAST = {
  1: ["Не коректно вказано кількість комірок", "bad"],
  2: ["Отримано іншу кількість комірок, ніж вказано", "bad"],
};
const ERR_OFF = {
  1: ["Некоректні налаштування", "bad"],
  2: ["Некоректна кількість комірок", "bad"],
  5: ["Втрата звʼязку з додатковими модулями", "bad"],
  16: ["Втрата звʼязку з додатковими модулями", "bad"],
  256: ["Критично низька напруга на одній із комірок", "bad"],
};
const ERR_OFF_EXT = {
  1: ["Некоректні налаштування (не вказано кількість комірок)", "bad"],
  2: ["Не вдалося відновити звʼязок з додатковими модулями", "bad"],
  3: ["Некоректна кількість комірок (знайдено іншу, ніж у налаштуваннях)", "bad"],
};

// Map an event/field name to its label + code dictionary.
const FIELD_LABEL = {
  bms_state: "BMS",
  charge_state: "Заряд",
  discharge_state: "Розряд",
  err_last: "Код помилки",
  err_off: "Код вимкнення",
  err_off_ext: "Розшир. код",
};
const FIELD_MAP = {
  bms_state: BMS_STATE,
  charge_state: CHARGE_STATE,
  discharge_state: DISCHARGE_STATE,
  err_last: ERR_LAST,
  err_off: ERR_OFF,
  err_off_ext: ERR_OFF_EXT,
};

function decode(map, code) {
  if (code == null) return null;
  return map[code] || [`Код ${code}`, "idle"];
}

// A colored pill for a decoded code.
function StatePill({ map, code }) {
  const res = decode(map, code);
  if (!res) return <span className="text-slate-400">—</span>;
  const [label, tone] = res;
  const bg = {
    ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
    info: "bg-sky-50 text-sky-700 border-sky-200",
    warn: "bg-amber-50 text-amber-700 border-amber-200",
    bad: "bg-red-50 text-red-700 border-red-200",
    idle: "bg-slate-50 text-slate-600 border-slate-200",
  }[tone] || "bg-slate-50 text-slate-600 border-slate-200";
  return (
    <span className={`inline-block rounded-md border px-2 py-0.5 text-xs font-medium ${bg}`}>
      {label}
    </span>
  );
}

function ErrTable({ title, map }) {
  return (
    <div className="rounded-lg border border-slate-100 p-3">
      <div className="mb-2 font-mono text-xs font-semibold text-slate-500">
        {title}
      </div>
      <div className="space-y-1.5">
        {Object.entries(map).map(([code, val]) => (
          <div key={code} className="flex gap-2 text-xs">
            <span className="w-8 shrink-0 font-mono font-semibold text-red-600">
              {code}
            </span>
            <span className="text-slate-700">{val[0]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


function Panel({ title, right, children, className = "", testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border border-slate-200 bg-white shadow-sm ${className}`}
    >
      {title && (
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">
            {title}
          </span>
          {right}
        </div>
      )}
      <div className="p-5">{children}</div>
    </div>
  );
}

// Wraps a chart and translates mouse-wheel gestures into X-axis (time) zoom.
// Uses a native non-passive wheel listener so we can preventDefault the page scroll.
function ZoomWheel({ onZoom, zoomed, onReset, resetTestId, className = "", children }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = (e) => {
      e.preventDefault();
      onZoom(e.clientX, el.getBoundingClientRect(), e.deltaY);
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  });
  return (
    <div ref={ref} className={`relative ${className}`}>
      {zoomed && (
        <button
          data-testid={resetTestId}
          onClick={onReset}
          className="absolute right-2 top-2 z-10 rounded-md border border-slate-200 bg-white/90 px-2 py-1 text-[11px] font-medium text-slate-600 shadow-sm backdrop-blur transition-colors hover:bg-slate-50"
        >
          Скинути зум
        </button>
      )}
      {children}
    </div>
  );
}

export default function BatteryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [metrics, setMetrics] = useState(["soc"]);
  const [batDate, setBatDate] = useState(new Date());
  const [batData, setBatData] = useState([]);
  const [cellData, setCellData] = useState({ cells: [], points: [] });
  const [tempData, setTempData] = useState({ cells: [], points: [] });
  const [hoverCell, setHoverCell] = useState(null);
  const [hoverTempCell, setHoverTempCell] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [stateLog, setStateLog] = useState([]);
  const [showErrRef, setShowErrRef] = useState(false);
  const [xZoom, setXZoom] = useState(null); // {start,end} ms, or null = full day

  // Memoize the cell lines so hovering (hoverCell state change) does NOT
  // re-render every line — that mass re-render was causing the flicker.
  // Depends on selectedCell so the picked cell is highlighted (others dimmed).
  const cellLines = useMemo(
    () => {
      const arr = cellData.cells.map((i) => {
        const isSel = selectedCell === i;
        const dim = selectedCell != null && !isSel;
        return (
          <Line
            key={i}
            type="monotone"
            dataKey={`c${i}`}
            name={`Комірка ${i}`}
            stroke={CELL_COLORS[(i - 1) % CELL_COLORS.length]}
            strokeWidth={isSel ? 3 : 1.2}
            strokeOpacity={dim ? 0.12 : 1}
            dot={false}
            activeDot={{
              r: 4,
              strokeWidth: 1,
              stroke: "#fff",
              onMouseOver: () => setHoverCell(i),
              onMouseEnter: () => setHoverCell(i),
            }}
            isAnimationActive={false}
            connectNulls
          />
        );
      });
      // Draw the selected line last so it sits on top of the dimmed ones.
      if (selectedCell != null) {
        const idx = cellData.cells.indexOf(selectedCell);
        if (idx > -1) arr.push(arr.splice(idx, 1)[0]);
      }
      return arr;
    },
    [cellData.cells, selectedCell]
  );

  const tempLines = useMemo(
    () => {
      const arr = tempData.cells.map((i) => {
        const isSel = selectedCell === i;
        const dim = selectedCell != null && !isSel;
        return (
          <Line
            key={i}
            type="monotone"
            dataKey={`c${i}`}
            name={`Комірка ${i}`}
            stroke={CELL_COLORS[(i - 1) % CELL_COLORS.length]}
            strokeWidth={isSel ? 3 : 1.2}
            strokeOpacity={dim ? 0.12 : 1}
            dot={false}
            activeDot={{
              r: 4,
              strokeWidth: 1,
              stroke: "#fff",
              onMouseOver: () => setHoverTempCell(i),
              onMouseEnter: () => setHoverTempCell(i),
            }}
            isAnimationActive={false}
            connectNulls
          />
        );
      });
      if (selectedCell != null) {
        const idx = tempData.cells.indexOf(selectedCell);
        if (idx > -1) arr.push(arr.splice(idx, 1)[0]);
      }
      return arr;
    },
    [tempData.cells, selectedCell]
  );

  const clearHoverCell = useCallback(() => setHoverCell(null), []);
  const clearHoverTemp = useCallback(() => setHoverTempCell(null), []);

  useEffect(() => {
    const load = () => getDevice(id).then(setDevice).catch(() => {});
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setXZoom(null);
    const d = batDate;
    const ds = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    getBatteryProfile(id, ds)
      .then((res) => setBatData((res.points || []).map((pt) => ({ ...pt, t: new Date(pt.t).getTime() }))))
      .catch(() => setBatData([]));
    getCellProfile(id, ds)
      .then((res) =>
        setCellData({
          cells: res.cells || [],
          points: (res.points || []).map((pt) => ({ ...pt, t: new Date(pt.t).getTime() })),
        })
      )
      .catch(() => setCellData({ cells: [], points: [] }));
    getCellTempProfile(id, ds)
      .then((res) =>
        setTempData({
          cells: res.cells || [],
          points: (res.points || []).map((pt) => ({ ...pt, t: new Date(pt.t).getTime() })),
        })
      )
      .catch(() => setTempData({ cells: [], points: [] }));
    getStateLog(id, ds)
      .then((res) => setStateLog(res.events || []))
      .catch(() => setStateLog([]));
    const iv = setInterval(() => {
      getStateLog(id, ds)
        .then((res) => setStateLog(res.events || []))
        .catch(() => {});
    }, 15000);
    return () => clearInterval(iv);
  }, [id, batDate]);

  if (!device) {
    return (
      <div className="min-h-screen bg-slate-100 pt-16">
        <TopNav />
        <div className="flex items-center justify-center py-24 text-slate-400">
          <Loader2 className="animate-spin" />
        </div>
      </div>
    );
  }

  const p = device.telemetry?.payload || {};
  const isHV = device.type === "viwat_hv";
  const voltMax = isHV ? 400 : 60;
  const currAbs = isHV ? 200 : 100;
  const soc = p.soc ?? null;
  const current = p.current ?? null;
  const voltage = p.total_voltage ?? null;
  const cellsObj = p.cell_voltages || {};
  const cells = Object.keys(cellsObj)
    .sort((a, b) => parseInt(a.split("_")[1]) - parseInt(b.split("_")[1]))
    .map((k, i) => ({ cell: i + 1, v: cellsObj[k] }));
  const cellVals = cells.map((c) => c.v).filter((x) => typeof x === "number");
  const maxV = cellVals.length ? Math.max(...cellVals) : null;
  const minV = cellVals.length ? Math.min(...cellVals) : null;
  // Only the FIRST cell reaching the max/min gets highlighted (avoid ties
  // colouring many bars at once).
  const maxCell = maxV != null ? cells.find((c) => c.v === maxV)?.cell : null;
  const minCell = minV != null ? cells.find((c) => c.v === minV)?.cell : null;
  const diffMv = maxV != null && minV != null ? Math.round((maxV - minV) * 1000) : null;
  const state =
    current == null ? "—" : current > 0.2 ? "Заряджання" : current < -0.2 ? "Розряджання" : "Спокій";
  const lastOnline = online(device.telemetry?.last_seen);
  const temps = [p.temperature1, p.temperature2, p.temperature3].filter((t) => t != null);
  const selected = METRICS.filter((m) => metrics.includes(m.key));
  const dayStartMs = kyivDayStartMs(batDate);
  const dayEndMs = dayStartMs + 86400000;
  const xStart = xZoom ? xZoom.start : dayStartMs;
  const xEnd = xZoom ? xZoom.end : dayEndMs;
  const xTicks = kyivTicks(dayStartMs).filter((t) => t >= xStart && t <= xEnd);
  // Wheel zoom: scroll up = zoom in, down = zoom out, anchored at the cursor.
  const zoomAt = (clientX, rect, deltaY) => {
    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const start = xZoom ? xZoom.start : dayStartMs;
    const end = xZoom ? xZoom.end : dayEndMs;
    const span = end - start;
    let newSpan = span * (deltaY < 0 ? 0.75 : 1.3333);
    if (newSpan >= 86400000) {
      setXZoom(null);
      return;
    }
    newSpan = Math.max(5 * 60000, newSpan); // min window: 5 minutes
    const center = start + frac * span;
    let ns = center - frac * newSpan;
    let ne = ns + newSpan;
    if (ns < dayStartMs) {
      ns = dayStartMs;
      ne = ns + newSpan;
    }
    if (ne > dayEndMs) {
      ne = dayEndMs;
      ns = ne - newSpan;
    }
    setXZoom({ start: ns, end: ne });
  };

  return (
    <div className="min-h-screen bg-slate-100 pt-16">
      <TopNav />
      <div className="mx-auto max-w-[1500px] px-6 py-6">
        <div className="mb-5 flex items-center gap-3">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/devices")}
            className="rounded-lg border border-slate-200 bg-white p-2 text-slate-600 transition-colors hover:bg-slate-50"
          >
            <ArrowLeft size={18} />
          </button>
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
            <BatteryCharging size={22} />
          </span>
          <div>
            <h1 className="font-display text-xl font-extrabold tracking-tight text-slate-900">
              {device.name}
            </h1>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Circle
                size={8}
                className={lastOnline ? "fill-emerald-500 text-emerald-500" : "fill-slate-300 text-slate-300"}
              />
              {lastOnline ? "В мережі" : "Немає звʼязку"} · {device.mqtt_topic}
            </div>
          </div>
        </div>

        {/* Gauges */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Panel testid="battery-soc-panel">
            <Gauge
              testid="gauge-soc"
              value={soc}
              min={0}
              max={100}
              unit="Заряд %"
              label="Заряд"
              color="#10B981"
            />
          </Panel>
          <Panel testid="battery-current-panel">
            <Gauge
              testid="gauge-current"
              value={current}
              min={-currAbs}
              max={currAbs}
              unit="A"
              label="Струм"
              color="#3B82F6"
              bipolar
            />
          </Panel>
          <Panel testid="battery-voltage-panel">
            <Gauge
              testid="gauge-voltage"
              value={voltage}
              min={0}
              max={voltMax}
              unit="В"
              label="Напруга"
              color="#EAB308"
            />
          </Panel>
        </div>

        {/* Imbalance + Status */}
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Panel title="Дисбаланс" testid="battery-imbalance-panel">
            <div className="space-y-4">
              <div>
                <div className="mb-1 flex justify-between text-xs text-slate-500">
                  <span>Макс. напруга комірки</span>
                  <span className="font-mono font-semibold text-slate-900">
                    {maxV != null ? `${maxV.toFixed(3)} V` : "—"}
                  </span>
                </div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-amber-400"
                    style={{ width: `${maxV ? ((maxV - 3) / 1.2) * 100 : 0}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="mb-1 flex justify-between text-xs text-slate-500">
                  <span>Мін. напруга комірки</span>
                  <span className="font-mono font-semibold text-slate-900">
                    {minV != null ? `${minV.toFixed(3)} V` : "—"}
                  </span>
                </div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-sky-500"
                    style={{ width: `${minV ? ((minV - 3) / 1.2) * 100 : 0}%` }}
                  />
                </div>
              </div>
              <div className="rounded-lg bg-slate-50 px-4 py-3 text-center">
                <span className="text-sm text-slate-500">Різниця напруг: </span>
                <span className="font-mono text-lg font-bold text-amber-500">
                  {diffMv != null ? `${diffMv} mV` : "—"}
                </span>
              </div>
            </div>
          </Panel>

          <Panel title="Статус заряд/розряд" testid="battery-status-panel">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-400">Стан</div>
                <div className="mt-1 font-semibold text-slate-900">{state}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-400">BMS</div>
                <div className="mt-1">
                  {p.bms_state != null ? (
                    <StatusValue map={BMS_STATE} code={p.bms_state} />
                  ) : (
                    <span
                      className={`font-semibold ${
                        lastOnline ? "text-emerald-600" : "text-slate-500"
                      }`}
                    >
                      {lastOnline ? "Працює" : "Немає зв'язку"}
                    </span>
                  )}
                </div>
              </div>
              {p.charge_state != null && (
                <div data-testid="battery-charge-state">
                  <div className="text-xs uppercase tracking-wide text-slate-400">
                    Заряд
                  </div>
                  <div className="mt-1">
                    <StatusValue map={CHARGE_STATE} code={p.charge_state} />
                  </div>
                </div>
              )}
              {p.discharge_state != null && (
                <div data-testid="battery-discharge-state">
                  <div className="text-xs uppercase tracking-wide text-slate-400">
                    Розряд
                  </div>
                  <div className="mt-1">
                    <StatusValue map={DISCHARGE_STATE} code={p.discharge_state} />
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-400">Цикли</div>
                <div className="mt-1 font-mono font-semibold text-slate-900">
                  {p.cycles ?? "—"}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-400">Комірок</div>
                <div className="mt-1 font-mono font-semibold text-slate-900">
                  {p.num_cells ?? cells.length}
                </div>
              </div>
              <div className="col-span-2 flex items-center gap-2 border-t border-slate-100 pt-3">
                <Thermometer size={16} className="text-slate-400" />
                <span className="text-xs text-slate-500">Температура:</span>
                <span className="font-mono text-sm font-semibold text-slate-900">
                  {temps.length ? temps.map((t) => `${t}°`).join(" / ") : "—"}
                </span>
              </div>
            </div>
          </Panel>
        </div>

        {/* State change journal (only for devices reporting BMS states) */}
        {p.bms_state != null && (
          <Panel
            title="Журнал станів за добу"
            className="mt-4"
            testid="battery-state-log-panel"
            right={
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <History size={14} />
                {stateLog.length} подій
              </div>
            }
          >
            {stateLog.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-400">
                Немає змін станів за обрану добу
              </div>
            ) : (
              <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
                {stateLog.map((e, i) => {
                  const map = FIELD_MAP[e.field] || {};
                  const toRes = decode(map, e.to);
                  const tone = toRes ? toRes[1] : "idle";
                  const alarm = tone === "bad" || tone === "warn";
                  const border =
                    tone === "bad"
                      ? "border-red-500"
                      : tone === "warn"
                      ? "border-amber-400"
                      : tone === "ok"
                      ? "border-emerald-400"
                      : "border-slate-300";
                  return (
                    <div
                      key={i}
                      data-testid="state-log-event"
                      className={`flex flex-wrap items-center gap-2 rounded-lg border-l-4 bg-slate-50 px-3 py-2 text-sm ${border}`}
                    >
                      <span className="w-11 shrink-0 font-mono text-xs text-slate-500">
                        {fmtKyivHM(e.ts)}
                      </span>
                      {alarm && (
                        <AlertTriangle size={14} className="text-amber-500" />
                      )}
                      <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">
                        {FIELD_LABEL[e.field] || e.field}
                      </span>
                      {e.initial ? (
                        <span className="flex items-center gap-1.5">
                          <span className="text-xs text-slate-400">початок:</span>
                          <StatePill map={map} code={e.to} />
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5">
                          <StatePill map={map} code={e.from} />
                          <ChevronRight size={13} className="text-slate-400" />
                          <StatePill map={map} code={e.to} />
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Panel>
        )}

        {/* Error code reference (from A1103 manual) */}
        {p.bms_state != null && (
          <Panel
            title="Довідник кодів помилок BMS"
            className="mt-4"
            testid="battery-err-ref-panel"
            right={
              <button
                type="button"
                data-testid="err-ref-toggle"
                onClick={() => setShowErrRef((v) => !v)}
                className="flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-500 transition-colors hover:bg-slate-50"
              >
                {showErrRef ? (
                  <ChevronDown size={14} />
                ) : (
                  <ChevronRight size={14} />
                )}
                {showErrRef ? "Згорнути" : "Показати"}
              </button>
            }
          >
            {showErrRef ? (
              <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
                <ErrTable title="BmsLastErrCode" map={ERR_LAST} />
                <ErrTable title="erroff (вимкнення)" map={ERR_OFF} />
                <ErrTable title="erroffExt (розширений)" map={ERR_OFF_EXT} />
              </div>
            ) : (
              <p className="text-xs text-slate-400">
                Розшифровка кодів помилок BMS згідно з інструкцією A1103.
                Ці коди не транслюються постійно — зʼявляються тут, щойно пристрій
                їх повідомить. Натисніть «Показати», щоб переглянути довідник.
              </p>
            )}
          </Panel>
        )}

        {/* 24h profile */}
        <Panel
          title="Графік за добу"
          testid="battery-profile-panel"
          className="mt-4 order-last"
          right={
            <PeriodPicker
              dayOnly
              value={{ mode: "day", date: batDate }}
              onChange={(v) => {
                setBatData([]);
                setBatDate(v.date);
              }}
            />
          }
        >
          <div className="mb-3 flex flex-wrap gap-2">
            {METRICS.map((m) => (
              <button
                key={m.key}
                data-testid={`bat-metric-${m.key}`}
                onClick={() =>
                  setMetrics((cur) =>
                    cur.includes(m.key) ? cur.filter((k) => k !== m.key) : [...cur, m.key]
                  )
                }
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  metrics.includes(m.key)
                    ? "bg-blue-600 text-white"
                    : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                {m.name}
              </button>
            ))}
          </div>
          <div className="mb-2 flex items-center gap-1.5 text-[11px] text-slate-400">
            <span>Прокрутіть колесом миші над графіком, щоб збільшити / зменшити масштаб часу</span>
          </div>
          <ZoomWheel onZoom={zoomAt} zoomed={!!xZoom} onReset={() => setXZoom(null)} resetTestId="bat-zoom-reset" className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={batData} margin={{ top: 8, right: 8, bottom: 4, left: -4 }}>
                <CartesianGrid stroke="#F1F5F9" vertical={false} />
                <XAxis
                  dataKey="t"
                  type="number"
                  domain={[xStart, xEnd]}
                  scale="time"
                  allowDataOverflow
                  ticks={xTicks}
                  tickFormatter={fmtKyivHM}
                  stroke="#94A3B8"
                  tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
                />
                {selected.length === 0 && (
                  <YAxis stroke="#94A3B8" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} width={48} />
                )}
                {selected.map((m, i) => (
                  <YAxis
                    key={m.key}
                    yAxisId={m.key}
                    orientation={i % 2 === 0 ? "left" : "right"}
                    stroke={m.color}
                    tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: m.color }}
                    tickFormatter={(v) => (Number.isInteger(v) ? v : v.toFixed(2))}
                    width={46}
                  />
                ))}
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                  labelFormatter={(t) => new Date(t).toUTCString().slice(17, 22)}
                  formatter={(v, name) => {
                    const m = METRICS.find((x) => x.name === name);
                    return [`${v}${m ? ` ${m.unit}` : ""}`, name];
                  }}
                />
                {selected.map((m) => (
                  <Line
                    key={m.key}
                    yAxisId={m.key}
                    type="monotone"
                    dataKey={m.key}
                    name={m.name}
                    stroke={m.color}
                    strokeWidth={1.6}
                    dot={false}
                    isAnimationActive={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </ZoomWheel>
        </Panel>

        {/* Cell voltages */}
        <Panel title={`Напруга комірок (${cells.length})`} className="mt-4" testid="battery-cells-panel">
          <div className="h-80 w-full">
            {cells.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-slate-400">
                Немає даних по комірках
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cells} margin={{ top: 8, right: 8, bottom: 4, left: -12 }}>
                  <CartesianGrid stroke="#F1F5F9" vertical={false} />
                  <XAxis
                    dataKey="cell"
                    stroke="#94A3B8"
                    tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
                    interval={0}
                  />
                  <YAxis
                    domain={[3, 3.6]}
                    stroke="#94A3B8"
                    tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }}
                    width={48}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: "1px solid #E2E8F0",
                      fontSize: 12,
                      fontFamily: "JetBrains Mono",
                    }}
                    formatter={(val) => [`${val} В`, "Напруга"]}
                    labelFormatter={(l) => `Комірка ${l}`}
                  />
                  <Bar
                    dataKey="v"
                    radius={[3, 3, 0, 0]}
                    cursor="pointer"
                    onClick={(d) => {
                      const c = d?.cell ?? d?.payload?.cell;
                      if (c != null) setSelectedCell((prev) => (prev === c ? null : c));
                    }}
                  >
                    {cells.map((c, i) => (
                      <ReCell
                        key={i}
                        fill={
                          c.cell === selectedCell
                            ? "#10B981"
                            : c.cell === maxCell
                            ? "#F59E0B"
                            : c.cell === minCell
                            ? "#EF4444"
                            : "#38BDF8"
                        }
                        stroke={c.cell === selectedCell ? "#065F46" : undefined}
                        strokeWidth={c.cell === selectedCell ? 1.5 : 0}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Panel>

        {/* Cell voltage history */}
        <Panel
          title="Напруга комірок за добу"
          className="mt-4"
          testid="battery-cell-history-panel"
          right={
            <PeriodPicker
              dayOnly
              value={{ mode: "day", date: batDate }}
              onChange={(v) => {
                setCellData({ cells: [], points: [] });
                setTempData({ cells: [], points: [] });
                setSelectedCell(null);
                setBatDate(v.date);
              }}
            />
          }
        >
          {cellData.cells.length > 0 && (
            <div className="mb-3">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-xs text-slate-500">
                  {selectedCell != null
                    ? `Підсвічено комірку ${selectedCell} — натисніть ще раз, щоб скинути`
                    : "Оберіть комірку, щоб підсвітити її лінію"}
                </span>
                {selectedCell != null && (
                  <button
                    type="button"
                    data-testid="cell-clear-btn"
                    onClick={() => setSelectedCell(null)}
                    className="rounded-md border border-slate-200 px-2 py-0.5 text-xs text-slate-500 transition-colors hover:bg-slate-50"
                  >
                    Скинути
                  </button>
                )}
              </div>
              <div className="flex max-h-24 flex-wrap gap-1 overflow-y-auto pr-1">
                {cellData.cells.map((i) => {
                  const active = selectedCell === i;
                  const color = CELL_COLORS[(i - 1) % CELL_COLORS.length];
                  return (
                    <button
                      key={i}
                      type="button"
                      data-testid={`cell-chip-${i}`}
                      onClick={() => setSelectedCell(active ? null : i)}
                      title={`Комірка ${i}`}
                      className={`flex h-6 min-w-[26px] items-center justify-center gap-1 rounded-md border px-1 text-[11px] font-mono transition-all ${
                        active
                          ? "border-slate-800 bg-slate-800 text-white"
                          : selectedCell != null
                          ? "border-slate-200 text-slate-400 hover:border-slate-300"
                          : "border-slate-200 text-slate-600 hover:border-slate-400"
                      }`}
                    >
                      <span
                        className="inline-block h-2 w-2 rounded-full"
                        style={{ background: color }}
                      />
                      {i}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <ZoomWheel onZoom={zoomAt} zoomed={!!xZoom} onReset={() => setXZoom(null)} resetTestId="cell-zoom-reset" className="h-96 w-full">
            {cellData.points.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-slate-400">
                Немає даних за обрану добу
              </div>
            ) : (
              <CellHistoryChart points={cellData.points} lines={cellLines} dayStartMs={dayStartMs} xStart={xStart} xEnd={xEnd} hover={hoverCell} onLeave={clearHoverCell} unit="В" digits={2} colors={CELL_COLORS} />
            )}
          </ZoomWheel>
        </Panel>

        {/* Cell temperature history */}
        {tempData.cells.length > 0 && (
          <Panel
            title="Температура комірок за добу"
            className="mt-4"
            testid="battery-cell-temp-panel"
          >
            <div className="mb-3">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-xs text-slate-500">
                  {selectedCell != null
                    ? `Підсвічено комірку ${selectedCell} — натисніть ще раз, щоб скинути`
                    : "Оберіть комірку, щоб підсвітити її лінію"}
                </span>
                {selectedCell != null && (
                  <button
                    type="button"
                    data-testid="temp-clear-btn"
                    onClick={() => setSelectedCell(null)}
                    className="rounded-md border border-slate-200 px-2 py-0.5 text-xs text-slate-500 transition-colors hover:bg-slate-50"
                  >
                    Скинути
                  </button>
                )}
              </div>
              <div className="flex max-h-24 flex-wrap gap-1 overflow-y-auto pr-1">
                {tempData.cells.map((i) => {
                  const active = selectedCell === i;
                  const color = CELL_COLORS[(i - 1) % CELL_COLORS.length];
                  return (
                    <button
                      key={i}
                      type="button"
                      data-testid={`temp-chip-${i}`}
                      onClick={() => setSelectedCell(active ? null : i)}
                      title={`Комірка ${i}`}
                      className={`flex h-6 min-w-[26px] items-center justify-center gap-1 rounded-md border px-1 text-[11px] font-mono transition-all ${
                        active
                          ? "border-slate-800 bg-slate-800 text-white"
                          : selectedCell != null
                          ? "border-slate-200 text-slate-400 hover:border-slate-300"
                          : "border-slate-200 text-slate-600 hover:border-slate-400"
                      }`}
                    >
                      <span
                        className="inline-block h-2 w-2 rounded-full"
                        style={{ background: color }}
                      />
                      {i}
                    </button>
                  );
                })}
              </div>
            </div>
            <ZoomWheel onZoom={zoomAt} zoomed={!!xZoom} onReset={() => setXZoom(null)} resetTestId="temp-zoom-reset" className="h-96 w-full">
              {tempData.points.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-slate-400">
                  Немає даних за обрану добу
                </div>
              ) : (
                <CellHistoryChart points={tempData.points} lines={tempLines} dayStartMs={dayStartMs} xStart={xStart} xEnd={xEnd} hover={hoverTempCell} onLeave={clearHoverTemp} unit="°C" digits={1} colors={CELL_COLORS} />
              )}
            </ZoomWheel>
          </Panel>
        )}
      </div>
    </div>
  );
}
